import { forwardRef, Ref, useCallback, useMemo } from "react";
import cx from "classnames";

import { SrOnly } from "../../../../../../components/SrOnly";

import { MultiSelectMenu } from "../../../../../../beta/components/MultiSelectMenu/MultiSelectMenu";
import { MultiSelectMenuOption } from "../../../../../../beta/components/MultiSelectMenu/types";
import {
  MultiselectAsyncCellEagerLoader,
  MultiselectAsyncCellPageLoader,
  MultiselectAsyncCellOffsetLoader,
  MultiselectAsyncCellGroupLoader,
} from "../../../types";

import { DataTableEditableCellEditIcon } from "./DataTableEditableCellEditIcon";
import { TableBodyCell } from "../../../base/cells/TableBodyCell";

import styles from "./DataTableEditableCells.module.scss";
import { EditableCellImperativeHandle } from "./types";
import { formatCellPositionString } from "../util/cellPositionHelpers";
import { mergeRefs } from "../../../../../../hooks/useMergeRefs/useMergeRefs";
import { BaseEditableCellProps } from "./types";
import { getColumnId, getCellValue } from "../util/cellTypeHelpers";
import { DataTableBodyImmutableCell } from "../cells/DataTableBodyImmutableCell";
import { CellFocusContext } from "../cells/CellFocusContext";
import { useEditableMenuCell } from "./useEditableMenuCell";

export type DataTableEditableMultiselectAsyncCellProps<T> =
  BaseEditableCellProps<T>;

/**
 * Renders an async editable multiselect cell in a DataTable. This component provides a
 * MultiSelectMenu popover that loads options asynchronously via a `loadOptions` function.
 * The loader receives a `context` object containing the current row data, enabling per-row
 * option loading.
 *
 * Features:
 * - Supports all four MultiSelectMenu lazy variants: eager, page, offset, and group
 * - Passes row context to `loadOptions` for per-row async option loading
 * - Auto-disables caching by default to prevent cross-row cache pollution
 * - Supports full MultiSelectMenu passthrough props (debounceMs, virtualize, selectAll, etc.)
 * - Live selection: `onChange` fires on each toggle
 * - Keyboard navigation consistent with other editable cell types
 *
 * **Value contract**: Unlike `mode: "multiselect"` (which stores ID arrays), the cell value
 * must be `MultiSelectMenuOption[]` — full option objects. The consumer stores them wholesale
 * from the `onChange` callback.
 *
 * @param props - The props for the DataTableEditableMultiselectAsyncCell component
 * @param props.cell - The TanStack table cell instance
 * @param props.cellProps - Props for styling and event handling of the cell container
 * @param props.cellPosition - The position of the cell in the table
 * @param props.children - The content to display when not editing
 * @param props.isHovered - Whether the cell is currently hovered
 * @param ref - Ref to the imperative handle for programmatic control
 * @returns The rendered async editable multiselect cell component
 */
export function DataTableEditableMultiselectAsyncCellInner<T>(
  props: DataTableEditableMultiselectAsyncCellProps<T>,
  ref: React.Ref<EditableCellImperativeHandle>,
) {
  const { cell, cellProps, cellPosition, children } = props;
  const { onKeyDown: onCellKeyDown, className, ...restCellProps } = cellProps;

  const columnId = getColumnId(cell);
  const cellValue = getCellValue(cell, columnId);

  const editConfig = cell.column.columnDef.meta?.editConfig;
  const asyncEditConfig =
    editConfig?.mode === "multiselect-async" ? editConfig : undefined;

  const isValidValueType = useMemo(() => {
    if (cellValue === null || cellValue === undefined) return true;
    if (Array.isArray(cellValue)) return true;
    return false;
  }, [cellValue]);

  const rowId = cell.row.id;
  const row = cell.row.original as T;

  const context = useMemo(() => ({ row, rowId }), [row, rowId]);

  const {
    mode: _mode,
    lazy,
    lazyOptions,
    loadOptions: cellLoader,
    onChange,
    cache: cacheProp,
    ...passthroughProps
  } = asyncEditConfig ?? {};

  // Curry context out of the consumer's loader to produce a MultiSelectMenu-compatible loader
  const loadOptions = useCallback(
    (searchValue: string, ...rest: unknown[]) => {
      if (!cellLoader) return [];
      if (lazy === "page") {
        const [pageNumber, pageSize] = rest as [number, number];
        return (cellLoader as MultiselectAsyncCellPageLoader<T>)(
          searchValue,
          pageNumber,
          pageSize,
          context,
        );
      }
      if (lazy === "offset") {
        const [offset, limit] = rest as [number, number];
        return (cellLoader as MultiselectAsyncCellOffsetLoader<T>)(
          searchValue,
          offset,
          limit,
          context,
        );
      }
      if (lazy === "group") {
        const [previousGroupKey] = rest as [string | number | null];
        return (cellLoader as MultiselectAsyncCellGroupLoader<T>)(
          searchValue,
          previousGroupKey,
          context,
        );
      }
      return (cellLoader as MultiselectAsyncCellEagerLoader<T>)(
        searchValue,
        context,
      );
    },
    [cellLoader, context, lazy],
  );

  // Auto-disable cache unless consumer explicitly provided one
  const cache = cacheProp ?? { enabled: false };

  const {
    tabIndex,
    cellRef,
    triggerClickRef,
    handleCellKeyDown,
    handleMenuKeyDown,
    handleImplicitClose,
    handleCellMouseDown,
    handleCellBlur,
    handleCellFocus,
    cellFocusContextValue,
  } = useEditableMenuCell({ cellPosition, onCellKeyDown, ref });

  // Value contract: cell value must be MultiSelectMenuOption[] (full objects)
  const selectedOptions = useMemo(() => {
    if (!Array.isArray(cellValue)) return [];
    return cellValue as MultiSelectMenuOption[];
  }, [cellValue]);

  if (!isValidValueType || !asyncEditConfig) {
    const { cellProps: _cellProps, ...immutableCellProps } = props;
    return <DataTableBodyImmutableCell {...immutableCellProps} />;
  }

  return (
    <MultiSelectMenu
      // Cast required: MultiSelectMenu uses a discriminated union on `lazy`, and the curried
      // `loadOptions` cannot satisfy a single overload after destructuring from the editConfig union.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      lazy={lazy as any}
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      loadOptions={loadOptions as any}
      lazyOptions={lazyOptions}
      cache={cache}
      {...passthroughProps}
      trigger={({
        ref: triggerRef,
        onClick,
        onKeyDown: menuKeyDown,
        "aria-controls": ariaControls,
        "aria-expanded": ariaExpanded,
        "aria-haspopup": ariaHaspopup,
        "data-state": dataState,
      }) => {
        triggerClickRef.current = onClick;
        const isOpen = ariaExpanded;
        return (
          <TableBodyCell
            ref={mergeRefs([
              triggerRef as Ref<HTMLElement>,
              cellRef as Ref<HTMLElement>,
            ])}
            tabIndex={tabIndex}
            type="data-table"
            {...restCellProps}
            className={cx(styles["data-table-body-cell-editable"], className)}
            onMouseDown={handleCellMouseDown(onClick)}
            onBlur={handleCellBlur}
            onFocus={handleCellFocus}
            data-cell-pos={formatCellPositionString(cellPosition)}
            aria-controls={ariaControls}
            aria-expanded={ariaExpanded}
            aria-haspopup={ariaHaspopup}
            data-state={dataState}
            onKeyDown={(e) => handleCellKeyDown(e, menuKeyDown, isOpen)}
          >
            <CellFocusContext.Provider value={cellFocusContextValue}>
              {children}
              {!isOpen && <DataTableEditableCellEditIcon />}
            </CellFocusContext.Provider>
            <SrOnly>
              {isOpen
                ? "Press Enter or Space to select cell values. Press Escape or F2 to close."
                : "Press Enter or F2 to edit cell value."}
            </SrOnly>
          </TableBodyCell>
        );
      }}
      label={String(
        cell.column.columnDef.meta?.columnDef?.headerLabel ?? "Select",
      )}
      value={selectedOptions}
      onSelectedOptionsChange={(selected) => {
        onChange?.(selected, rowId);
      }}
      onMenuKeyDown={handleMenuKeyDown}
      onImplicitClose={handleImplicitClose}
    />
  );
}

export const DataTableEditableMultiselectAsyncCell = Object.assign(
  forwardRef(DataTableEditableMultiselectAsyncCellInner) as <T>(
    props: DataTableEditableMultiselectAsyncCellProps<T> & {
      ref?: React.Ref<EditableCellImperativeHandle>;
    },
  ) => React.ReactElement,
  { displayName: "DataTableEditableMultiselectAsyncCell" },
);
